import { Link, useParams, useSearchParams } from "react-router-dom";
import { Download, Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { Seo } from "@/components/site/Seo";
import { PageHero, Breadcrumbs } from "@/components/site/PageHero";
import { Markdown } from "@/components/site/Markdown";
import { Img } from "@/components/site/Img";
import { Reveal } from "@/components/site/Reveal";
import { ContactForm } from "@/components/forms/ContactForm";
import { PortalButton } from "@/components/site/Header";
import { AlertSignup } from "@/components/site/AlertSignup";
import { BACKEND, PHONE, PHONE_HREF, EMAIL } from "@/lib/api";
import NotFound from "./NotFound";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(0)} MB` : b ? `${Math.round(b / 1e3)} KB` : "");

function Chips({ items, active, onPick, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={() => onPick("")} className={`border px-3 py-1.5 text-xs uppercase tracking-wider ${!active ? "border-[color:var(--ice)] text-[color:var(--ice)]" : "border-[color:var(--line-2)] text-slate-400 hover:text-white"}`} data-testid={`${testPrefix}-all`}>All</button>
      {(items || []).map((c) => (
        <button key={c.id} onClick={() => onPick(c.slug)} className={`border px-3 py-1.5 text-xs uppercase tracking-wider ${active === c.slug ? "border-[color:var(--ice)] text-[color:var(--ice)]" : "border-[color:var(--line-2)] text-slate-400 hover:text-white"}`} data-testid={`${testPrefix}-${c.slug}`}>{c.name}</button>
      ))}
    </div>
  );
}

function useParamState(key) {
  const [sp, setSp] = useSearchParams();
  const v = sp.get(key) || "";
  return [v, (x) => { const n = new URLSearchParams(sp); x ? n.set(key, x) : n.delete(key); setSp(n); }];
}

export function Downloads() {
  const { data: page } = useFetch("/pages/downloads");
  const { data: cats } = useFetch("/simple-categories/download");
  const [cat, setCat] = useParamState("category");
  const [q, setQ] = useParamState("q");
  const { data: files } = useFetch("/downloads", { category: cat || undefined, q: q || undefined });
  return (
    <div data-testid="downloads-page">
      <Seo path="/software-downloads" />
      <PageHero page={page} crumbs={[["/", "Home"], [null, "Software & Downloads"]]} compact />
      <section className="container-x py-12">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <Chips items={cats} active={cat} onPick={setCat} testPrefix="downloads-category" />
          <form onSubmit={(e) => { e.preventDefault(); setQ(e.target.q.value.trim()); }} role="search"><input name="q" defaultValue={q} placeholder="Search downloads…" className="field lg:!w-72" data-testid="downloads-search-input" /></form>
        </div>
        <ul className="divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
          {(files || []).map((f) => (
            <li key={f.id} className="flex flex-col gap-4 py-6 sm:flex-row sm:items-center sm:justify-between" data-testid={`download-item-${f.slug}`}>
              <Link to={`/software-downloads/${f.slug}`} className="group flex items-start gap-4">
                {f.image && <Img src={f.image.url} alt={f.image.alt} sizes="64px" className="h-16 w-16 shrink-0 border border-[color:var(--line)] object-cover" />}
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--bronze)]">{f.featured && "★ Featured · "}{f.brand}{f.version && ` · v${f.version}`}</p>
                  <h2 className="mt-1 font-display text-2xl font-semibold uppercase group-hover:text-[color:var(--ice)]">{f.title}</h2>
                  <p className="mt-1 max-w-2xl text-sm text-slate-400">{f.short_description || f.description?.replace(/<[^>]+>/g, "").slice(0, 180)}</p>
                  <p className="mt-1 font-mono text-xs text-slate-500">{f.supported_os} {f.file_name} {fmtSize(f.file_size)}</p>
                </div>
              </Link>
              {f.available ? <a href={`${BACKEND}/api/downloads/${f.slug}/file`} className="btn-ice shrink-0" rel="nofollow" data-testid={`download-button-${f.slug}`}><Download className="h-4 w-4" />Download</a>
                : <span className="font-mono text-xs text-slate-500">Available on request</span>}
            </li>
          ))}
          {files?.length === 0 && <li className="py-10 text-slate-400" data-testid="downloads-empty">No downloads match.</li>}
        </ul>
        <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_340px]">
          <Markdown>{page?.body}</Markdown>
          <div className="space-y-6">
            <AlertSignup />
            <div className="panel p-6"><p className="eyebrow mb-3">Dealer files</p><p className="mb-4 text-sm text-slate-400">Tuning files and credits are in the customer portal.</p><PortalButton testId="downloads-portal-button" /></div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function DownloadDetail() {
  const { slug } = useParams();
  const { data: f, error } = useFetch(`/downloads/${slug}`);
  if (error) return <NotFound />;
  if (!f) return <div className="container-x py-24"><div className="h-96 animate-pulse bg-[color:var(--bg-2)]" /></div>;
  const meta = [["Version", f.version], ["Manufacturer", f.brand], ["Released", f.release_date && fmtDate(f.release_date)], ["Supported OS", f.supported_os], ["File", f.file_name && `${f.file_name} ${fmtSize(f.file_size)}`], ["Category", f.category?.name]].filter(([, v]) => v);
  return (
    <article className="container-x py-12" data-testid="download-detail-page">
      <Seo path={`/software-downloads/${f.slug}`} />
      <Breadcrumbs items={[["/", "Home"], ["/software-downloads", "Software & Downloads"], [null, f.title]]} />
      <div className="grid gap-12 lg:grid-cols-[1fr_340px]">
        <div>
          <h1 className="font-display text-4xl font-bold uppercase sm:text-5xl" data-testid="page-h1">{f.title}</h1>
          <p className="mt-4 text-slate-300">{f.short_description}</p>
          <Markdown className="prose-jt mt-8" testId="download-description">{f.description}</Markdown>
          {f.install_notes && <><h2 className="mt-10 font-display text-2xl uppercase">Installation notes</h2><Markdown className="prose-jt mt-4">{f.install_notes}</Markdown></>}
          {f.changelog && <><h2 className="mt-10 font-display text-2xl uppercase">Changelog</h2><Markdown className="prose-jt mt-4">{f.changelog}</Markdown></>}
        </div>
        <aside className="panel h-fit space-y-4 p-6 lg:sticky lg:top-28">
          {f.image && <Img src={f.image.url} alt={f.image.alt} sizes="340px" className="w-full" />}
          <dl className="space-y-2 text-sm">{meta.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="font-mono text-[11px] uppercase text-slate-500">{k}</dt><dd className="text-right text-slate-200">{v}</dd></div>)}</dl>
          {f.available ? <a href={`${BACKEND}/api/downloads/${f.slug}/file`} className="btn-ice w-full" rel="nofollow" data-testid="download-detail-button"><Download className="h-4 w-4" />Download</a> : <p className="text-sm text-slate-500">Available on request.</p>}
        </aside>
      </div>
    </article>
  );
}

export function Blog() {
  const { data: page } = useFetch("/pages/blog");
  const { data: cats } = useFetch("/simple-categories/post");
  const [cat, setCat] = useParamState("category");
  const { data: posts } = useFetch("/posts", { category: cat || undefined });
  const featured = !cat ? (posts || []).filter((p) => p.featured) : [];
  const rest = (posts || []).filter((p) => !featured.includes(p));
  const Card = ({ p, big }) => (
    <Link to={`/blog/post/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
      <div className={`${big ? "aspect-[21/9]" : "aspect-[16/10]"} overflow-hidden bg-[color:var(--bg-3)]`}>{p.cover_image && <Img src={p.cover_image.url} alt={p.cover_image.alt} sizes={big ? "100vw" : "(max-width:768px) 100vw, 33vw"} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}</div>
      <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">{p.featured && <span className="text-[color:var(--bronze)]">Featured · </span>}{fmtDate(p.published_at)}</p>
      <h2 className={`mt-2 font-display font-semibold uppercase leading-tight group-hover:text-[color:var(--ice)] ${big ? "text-4xl" : "text-2xl"}`}>{p.title}</h2>
      <p className="mt-3 text-sm text-slate-400">{p.excerpt}</p>
    </Link>
  );
  return (
    <div data-testid="blog-page">
      <Seo path="/blog" />
      <PageHero page={page} crumbs={[["/", "Home"], [null, "Guides & News"]]} compact />
      <section className="container-x py-12">
        {cats?.length > 0 && <div className="mb-10"><Chips items={cats} active={cat} onPick={setCat} testPrefix="blog-category" /></div>}
        {featured.length > 0 && <div className="mb-14 grid gap-8" data-testid="blog-featured">{featured.map((p) => <Card key={p.id} p={p} big />)}</div>}
        <div className="grid gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 80}><Card p={p} /></Reveal>)}
        </div>
        {posts?.length === 0 && <p className="text-slate-400" data-testid="blog-empty">No articles in this category yet.</p>}
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { data: p, error } = useFetch(`/posts/${slug}`);
  if (error) return <NotFound />;
  if (!p) return <div className="container-x py-24"><div className="h-96 animate-pulse bg-[color:var(--bg-2)]" /></div>;
  return (
    <article className="container-x max-w-3xl py-12" data-testid="blog-post-page">
      <Seo path={`/blog/post/${p.slug}`} />
      <Breadcrumbs items={[["/", "Home"], ["/blog", "Guides & News"], [null, p.title]]} />
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[color:var(--bronze)]">{fmtDate(p.published_at)} · {p.author}{(p.categories || []).map((c) => <Link key={c.id} to={`/blog?category=${c.slug}`} className="ml-2 text-slate-400 hover:text-white">· {c.name}</Link>)}</p>
      <h1 className="mt-4 font-display text-4xl font-bold uppercase leading-[0.95] sm:text-5xl lg:text-6xl" data-testid="page-h1">{p.title}</h1>
      {p.archived_notice && <div className="mt-8 border border-amber-400/50 bg-amber-400/10 p-5 text-sm font-medium text-amber-100" role="note" data-testid="blog-archived-notice">{p.archived_notice} <Link to="/shop" className="ml-1 underline">Visit the shop</Link></div>}
      {p.cover_image && <div className="mt-10 overflow-hidden border border-[color:var(--line)]"><Img src={p.cover_image.url} alt={p.cover_image.alt} eager sizes="(max-width:768px) 100vw, 768px" className="w-full" /></div>}
      <Markdown className="prose-jt mt-10" testId="blog-post-content">{p.content}</Markdown>
      {p.cta_label && p.cta_url && (
        <div className="mt-10">{p.cta_url.startsWith("http")
          ? <a href={p.cta_url} target="_blank" rel="noopener" className="btn-ice" data-testid="blog-post-cta">{p.cta_label}</a>
          : <Link to={p.cta_url} className="btn-ice" data-testid="blog-post-cta">{p.cta_label}</Link>}</div>
      )}
      {p.tags?.length > 0 && <p className="mt-8 font-mono text-xs text-slate-500">{p.tags.map((t) => `#${t}`).join("  ")}</p>}
      <div className="bronze-rule my-12" />
      <Link to="/blog" className="btn-ghost" data-testid="blog-back">All news</Link>
    </article>
  );
}

export function Contact() {
  const { data: page } = useFetch("/pages/contact");
  return (
    <div data-testid="contact-page">
      <Seo path="/contact" />
      <PageHero page={page} crumbs={[["/", "Home"], [null, "Contact"]]} compact />
      <section className="container-x grid gap-14 py-16 lg:grid-cols-[340px_1fr]">
        <div className="space-y-6">
          {[[Phone, "Phone", PHONE, PHONE_HREF, "contact-phone-link"], [Mail, "Email", EMAIL, `mailto:${EMAIL}`, "contact-email-link"], [MapPin, "Location", "Skipton, North Yorkshire", null, "contact-location"]].map(([I, l, v, href, tid]) => (
            <div key={l} className="panel flex items-start gap-4 p-5">
              <I className="mt-1 h-5 w-5 text-[color:var(--bronze)]" />
              <div><p className="eyebrow">{l}</p>{href ? <a href={href} className="mt-1 block text-lg text-slate-100 hover:text-[color:var(--ice)]" data-testid={tid}>{v}</a> : <p className="mt-1 text-lg" data-testid={tid}>{v}</p>}</div>
            </div>
          ))}
          <div className="panel p-5"><p className="eyebrow mb-2">Dealers</p><p className="mb-4 text-sm text-slate-400">File requests go through the portal.</p><PortalButton testId="contact-portal-button" /></div>
          <a href="/remappingfranchise" className="flex items-center gap-2 text-sm text-[color:var(--ice)] hover:underline">Applying to become a dealer? <ExternalLink className="h-3 w-3" /></a>
        </div>
        <div className="panel chamfer p-6 sm:p-10">
          <h2 className="mb-8 font-display text-3xl font-bold uppercase">Send an enquiry</h2>
          <ContactForm />
        </div>
      </section>
    </div>
  );
}
